import { FC, useMemo } from 'react';
import styled from 'styled-components';

import { TasksTable } from '@/features/vfo/components/TasksTable';
import { Tabs } from '@/features/vfo/uikit';
import { breakpoints } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useAuthStore } from '@/shared/store/auth.store';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { useTasks } from '../hooks/useTasks';

type TasksListProps = {
  showAllTab?: boolean;
  caseId?: string;
};

enum TableTabs {
  ALL = 'ALL',
  ASSIGNED_TO_ME = 'ASSIGNED_TO_ME',
  CREATED_BY_ME = 'CREATED_BY_ME',
}

export const TasksList: FC<TasksListProps> = ({ showAllTab = true, caseId }) => {
  const { t } = useTranslation('features/vfo', {
    keyPrefix: 'tasksList',
  });
  const { account } = useAuthStore();
  const { tasks: allTasks, tasksCreatedByMe } = useTasks();

  const tasks = useMemo(() => {
    const allFiltered = allTasks.filter((task) => !caseId || task.caseId === caseId);
    return {
      all: allFiltered,
      assignedToMe: allFiltered.filter((task) => task.assignee?.profileId === account?.id),
      createdByMe: tasksCreatedByMe.filter((task) => !caseId || task.caseId === caseId),
    };
  }, [account, allTasks, tasksCreatedByMe, caseId]);

  const defaultActiveKey = useMemo(() => {
    if (showAllTab) return TableTabs.ALL;
    if (tasks.assignedToMe.length === 0 && tasks.createdByMe.length !== 0) {
      return TableTabs.CREATED_BY_ME;
    }
    return TableTabs.ASSIGNED_TO_ME;
  }, [tasks]);

  const items = useMemo(() => {
    const arr = [
      {
        key: TableTabs.ASSIGNED_TO_ME,
        label: (
          <TabLabel>
            <span>{t('assignedToMe')}</span>
            <Count>{tasks.assignedToMe.length}</Count>
          </TabLabel>
        ),
        children: <TasksTable minWidth={700} tasks={tasks.assignedToMe} />,
      },
      {
        key: TableTabs.CREATED_BY_ME,
        label: (
          <TabLabel>
            <span>{t('createdByMe')}</span>
            <Count>{tasks.createdByMe.length}</Count>
          </TabLabel>
        ),
        children: <TasksTable minWidth={700} tasks={tasks.createdByMe} />,
      },
    ];

    if (showAllTab) {
      arr.unshift({
        key: TableTabs.ALL,
        label: (
          <TabLabel>
            <span>{t('allTasks')}</span>
            <Count>{tasks.all.length}</Count>
          </TabLabel>
        ),
        children: <TasksTable minWidth={700} tasks={tasks.all} />,
      });
    }
    return arr;
  }, [showAllTab, tasks, t]);

  return (
    <Container>
      <Tabs defaultActiveKey={defaultActiveKey} items={items} />
    </Container>
  );
};

const Container = styled.div`
  background: ${COLORS.white};
  padding: 24px;
  border-radius: 24px;

  @container ${breakpoints.sm} {
    padding: 16px;
  }
`;

const TabLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Count = styled.div`
  color: ${COLORS.systemDark300};
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 6px;
  background: ${COLORS.systemDark50A};
  font-weight: 400;
  line-height: 16px;
`;
